import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import { Media } from '@/js/libs/ui';
import { Table, Checkbox, TableCellContentType } from '@/js/libs/ui/src/V2';

import type { SortProps } from 'src/hooks/useSortAndOrderState';
import type { ColumnComponentPropsV2, ColumnV2 } from 'src/types';

export interface TableHeaderProps<
  TColumnName extends string,
  TColumnProps extends ColumnComponentPropsV2<TColumnName> = ColumnComponentPropsV2<TColumnName>,
> {
  getSortProps?: SortProps<TColumnName>;
  columns: ColumnV2<TColumnName, TColumnProps>[];
  isSticky?: boolean;
}

const TableHeader = <
  TColumnName extends string,
  TColumnProps extends ColumnComponentPropsV2<TColumnName> = ColumnComponentPropsV2<TColumnName>,
>({
  columns,
  getSortProps,
  isSticky,
}: TableHeaderProps<TColumnName, TColumnProps>) => {
  const [t] = useTranslation(['backend', 'common']);
  const isMobile = useMediaQuery({ query: Media.xsMax });

  return (
    <Table.HeadRow>
      {columns.map((column) => {
        const { name, title, toggleSelectAll, hidden } = column;
        const sortField = (column.customSortKey as TColumnName) ?? column.name;

        if (hidden || (isMobile && column.contentType === TableCellContentType.CHECKBOX && !toggleSelectAll)) {
          return null;
        }

        return (
          <Table.Head
            {...column}
            key={name}
            order={column.sortable && getSortProps ? getSortProps(sortField).order : undefined}
            handleChangeOrder={column.sortable && getSortProps ? getSortProps(sortField).handleChangeOrder : undefined}
            $sticky={isSticky}>
            {title}
            {toggleSelectAll && (
              <Checkbox
                label={t('common:SELECT_ALL')}
                checked={toggleSelectAll.checked}
                onChange={toggleSelectAll.onChange}
                disabled={toggleSelectAll.disabled}
                indeterminate={toggleSelectAll.indeterminate}
                dataTestId={toggleSelectAll.dataTestId}
              />
            )}
          </Table.Head>
        );
      })}
    </Table.HeadRow>
  );
};

export default TableHeader;
